import { deleteUser } from "firebase/auth";
import { doc, getDoc, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { createDefaultProfile } from "../../game/engine/gameState";
import { generateUsernameCandidate, normalizeUsername, validateUsername } from "../../game/utils/usernames";
import { getFirebaseAuth, getFirebaseDb, isFirebaseConfigured } from "./firebaseApp";

const requireDb = () => {
	const db = getFirebaseDb();
	if (!isFirebaseConfigured || !db) {
		throw new Error("Firestore is not configured. Add Firebase environment variables first.");
	}
	return db;
};

const playerRef = (db, uid) => doc(db, "players", uid);
const usernameRef = (db, usernameLower) => doc(db, "usernames", usernameLower);

const buildProfileRecord = (user, username, existingProfile = {}) => ({
	...createDefaultProfile(user, existingProfile),
	username,
	usernameLower: normalizeUsername(username),
	lastSeenAt: Date.now(),
});

export const getProfile = async (uid) => {
	if (!uid) return null;
	const snapshot = await getDoc(playerRef(requireDb(), uid));
	return snapshot.exists() ? snapshot.data() : null;
};

export const checkUsernameAvailability = async (username, currentUid = null) => {
	const validation = validateUsername(username);
	if (!validation.ok) return validation;

	const db = requireDb();
	const normalized = normalizeUsername(validation.username);
	const snapshot = await getDoc(usernameRef(db, normalized));
	if (!snapshot.exists()) {
		return { ok: true, available: true, username: validation.username, normalized };
	}

	const ownerUid = snapshot.data()?.uid || null;
	if (currentUid && ownerUid === currentUid) {
		return { ok: true, available: true, username: validation.username, normalized };
	}

	return {
		ok: false,
		available: false,
		username: validation.username,
		normalized,
		reason: "That username is already taken.",
	};
};

export const reserveUsername = async ({ user, username, previousProfile = null }) => {
	if (!user?.uid) throw new Error("You must be logged in to reserve a username.");

	const db = requireDb();
	const availability = await checkUsernameAvailability(username, user.uid);
	if (!availability.ok) throw new Error(availability.reason);

	const nextProfile = buildProfileRecord(user, availability.username, previousProfile || {});
	const previousLower = normalizeUsername(previousProfile?.username || "");
	const nextLower = nextProfile.usernameLower;

	await runTransaction(db, async (transaction) => {
		const nextNameDoc = usernameRef(db, nextLower);
		const nextNameSnapshot = await transaction.get(nextNameDoc);
		const previousNameDoc = previousLower && previousLower !== nextLower ? usernameRef(db, previousLower) : null;
		const previousSnapshot = previousNameDoc ? await transaction.get(previousNameDoc) : null;

		if (nextNameSnapshot.exists() && nextNameSnapshot.data()?.uid !== user.uid) {
			throw new Error("That username is already taken.");
		}

		transaction.set(nextNameDoc, {
			uid: user.uid,
			username: nextProfile.username,
			usernameLower: nextProfile.usernameLower,
			updatedAt: serverTimestamp(),
		});

		if (previousNameDoc && previousSnapshot?.exists() && previousSnapshot.data()?.uid === user.uid) {
			transaction.delete(previousNameDoc);
		}

		transaction.set(playerRef(db, user.uid), {
			...nextProfile,
			uid: user.uid,
			updatedAt: serverTimestamp(),
		}, { merge: true });
	});

	return nextProfile;
};

export const generateUniqueUsername = async (currentUid = null) => {
	for (let attempt = 0; attempt < 24; attempt += 1) {
		const candidate = generateUsernameCandidate();
		const availability = await checkUsernameAvailability(candidate, currentUid);
		if (availability.ok) return availability.username;
	}
	throw new Error("Could not generate a unique username right now. Try again.");
};

export const ensureUserProfile = async (user) => {
	if (!user?.uid) return null;

	const existing = await getProfile(user.uid);
	if (existing?.username) {
		await setDoc(playerRef(requireDb(), user.uid), { lastSeenAt: Date.now(), updatedAt: serverTimestamp() }, { merge: true });
		return existing;
	}

	const username = await generateUniqueUsername(user.uid);
	return reserveUsername({
		user,
		username,
		previousProfile: existing,
	});
};

export const createAccountProfile = async ({ user, username }) => {
	try {
		const finalUsername = username || await generateUniqueUsername(user?.uid);
		return await reserveUsername({ user, username: finalUsername, previousProfile: null });
	} catch (error) {
		try {
			const auth = getFirebaseAuth();
			if (auth?.currentUser?.uid === user?.uid) await deleteUser(auth.currentUser);
		} catch (cleanupError) {
			console.warn("Could not clean up partially created auth user.", cleanupError);
		}
		throw error;
	}
};
